import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import CustomPagination from '../pagination/CustomPagination';
import { Box, Card, Button, CardHeader, CardContent, IconButton, useTheme } from '@mui/material';
import Grid from "@mui/material/Grid2";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx-js-style';
import UserDialog from './UserDialog';

// NoRowsOverlay 컴포넌트 추가
import { styled } from '@mui/material/styles';

const StyledGridOverlay = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  width: '100%',
  '& .no-rows-primary': {
    fill: '#3D4751',
    ...theme.applyStyles('light', {
      fill: '#AEB8C2',
    }),
  },
  '& .no-rows-secondary': {
    fill: '#1D2126',
    ...theme.applyStyles('light', {
      fill: '#E8EAED',
    }),
  },
}));

function CustomNoRowsOverlay() {
  return (
    <StyledGridOverlay>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        width={96}
        viewBox="0 0 452 257"
        aria-hidden
        focusable="false"
      >
        <path
          className="no-rows-primary"
          d="M348 69c-46.392 0-84 37.608-84 84s37.608 84 84 84 84-37.608 84-84-37.608-84-84-84Zm-104 84c0-57.438 46.562-104 104-104s104 46.562 104 104-46.562 104-104 104-104-46.562-104-104Z"
        />
        <path
          className="no-rows-primary"
          d="M308.929 113.929c3.905-3.905 10.237-3.905 14.142 0l63.64 63.64c3.905 3.905 3.905 10.236 0 14.142-3.906 3.905-10.237 3.905-14.142 0l-63.64-63.64c-3.905-3.905-3.905-10.237 0-14.142Z"
        />
        <path
          className="no-rows-primary"
          d="M308.929 191.711c-3.905-3.906-3.905-10.237 0-14.142l63.64-63.64c3.905-3.905 10.236-3.905 14.142 0 3.905 3.905 3.905 10.237 0 14.142l-63.64 63.64c-3.905 3.905-10.237 3.905-14.142 0Z"
        />
        <path
          className="no-rows-secondary"
          d="M0 10C0 4.477 4.477 0 10 0h380c5.523 0 10 4.477 10 10s-4.477 10-10 10H10C4.477 20 0 15.523 0 10ZM0 59c0-5.523 4.477-10 10-10h231c5.523 0 10 4.477 10 10s-4.477 10-10 10H10C4.477 69 0 64.523 0 59ZM0 106c0-5.523 4.477-10 10-10h203c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 153c0-5.523 4.477-10 10-10h195.5c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 200c0-5.523 4.477-10 10-10h203c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 247c0-5.523 4.477-10 10-10h231c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10Z"
        />
      </svg>
      <Box sx={{ mt: 2 }}>표시할 데이터가 없습니다.</Box>
    </StyledGridOverlay>
  );
}

const UsersPage = () => {
  const user = useSelector((state) => state.auth.user);
  const theme = useTheme();
  const apiRef = useGridApiRef();
  const [users, setUsers] = useState([]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });
  const [selectionModel, setSelectionModel] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = () => {
    axios.post('/backend/project/getUserList')
      .then(res => {
        setUsers(res.data);
      })
      .catch(err => {
        console.error(err);
      });
  };

  const handleNewUser = () => {
    setSelectedUser(null);
    setDialogOpen(true);
  };

  const handleEditUser = (user_no, event) => {
    event.stopPropagation();
    const user = users.find(u => u.user_no === user_no);
    setSelectedUser(user);
    setDialogOpen(true);
  };

  const handleDeleteUser = (user_no, event) => {
    const ws = XLSX.utils.json_to_sheet(users);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Users");
    XLSX.writeFile(wb, `Users_List.xlsx`);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedUser(null);
  };

  const columns = [
    { 
      field: 'user_no', 
      headerName: '#', 
      width: 70,
      headerAlign: 'center',
      align: 'center'
    },
    { 
      field: 'login_id', 
      headerName: '아이디', 
      width: 120,
      headerAlign: 'center',
      align: 'center'
    },
    { 
      field: 'user_name', 
      headerName: '이름', 
      width: 120,
      headerAlign: 'center',
      align: 'center'
    },
    { 
      field: 'email', 
      headerName: '이메일', 
      width: 200,
      headerAlign: 'center',
      align: 'center'
    },
    { 
      field: 'user_type', 
      headerName: '권한', 
      width: 120,
      headerAlign: 'center',
      align: 'center'
    },
    { 
      field: 'team_name', 
      headerName: '팀', 
      width: 150,
      headerAlign: 'center',
      align: 'center'
    },
    { 
      field: 'join_date', 
      headerName: '입사일', 
      width: 120,
      headerAlign: 'center',
      align: 'center'
    },
    {
      field: 'created_at',
      headerName: '생성일',
      width: 180,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const date = new Date(params.value);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
      }
    },
    {
      field: 'actions',
      headerName: '편집',
      width: 120,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <IconButton onClick={(event) => handleEditUser(row.user_no, event)} size="small">
            <EditIcon />
          </IconButton>
          <IconButton onClick={(event) => handleDeleteUser(row.user_no, event)} size="small">
            <DeleteIcon />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Grid container spacing={3} sx={{ height: "100%" }}>
      <Grid size={{ xs: 12 }} sx={{ height: "100%"}}>
        <Card sx={{
          height: "100%",
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '0',
          bgcolor: theme.palette.mode === 'dark' 
            ? 'var(--mui-palette-background-default)' 
            : 'var(--mui-palette-background-paper)'
        }}>
          <CardHeader
            title="계정 리스트"
            action={
              <Box display="flex" gap={1}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleNewUser}
                >
                  신규 생성
                </Button>
              </Box>
            }
          />
          <CardContent sx={{ flexGrow: 1, overflow: "auto" }}>
            <DataGrid
              apiRef={apiRef}
              rows={users}
              columns={columns}
              pageSize={paginationModel.pageSize}
              rowsPerPageOptions={[20, 50, 100]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              checkboxSelection
              disableRowSelectionOnClick
              onRowSelectionModelChange={(newSelection) => {
                setSelectionModel(newSelection);
              }}
              getRowId={(row) => row.user_no}
              slots={{
                pagination: CustomPagination,
                noRowsOverlay: CustomNoRowsOverlay
              }}
              sx={{ height: "100%" }}
            />
          </CardContent>
        </Card>
      </Grid>
      <UserDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        userData={selectedUser}
        onSuccess={fetchUsers}
      />
    </Grid>
  );
};

export default UsersPage;